import React, { useEffect, useMemo, useRef, useState } from "react";

import { SelectChangeEvent, Tooltip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { Add as AddIcon } from "@mui/icons-material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  RowActionsExtra,
  Icons,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color } from "../../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { Group } from "../../../../../../models/group";
import useStyles, { ButtonText } from "./styles";
import useActions from "./useAction";
import { GroupType, Status } from "Util/enums";
import { GroupDrawer, GroupDrawerHandles } from "./components/GroupDrawer";
import Select from "Components/Form/Select";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";
import { format } from "date-fns";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

const Groups = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const {
    groups,
    hasErrors,
    total,
    page,
    pageSize,
    isLoading,
    setHasErrors,
    setPageSize,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    statusConverter,
    showAllStatusCode,
  } = useActions();
  const groupDrawerRef = useRef<GroupDrawerHandles>(null);
  const [filterValue, setFilterValue] = useState<string>(showAllStatusCode);
  const { currentUser, userPermissions } = useAppState();
  const [groupPermission, setGroupPermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  const groupStatus: { value: string; name: string }[] = useMemo(
    () => [
      { value: showAllStatusCode, name: translate(showAllStatusCode) },
      { value: Status.ACTIVE, name: translate(Status.ACTIVE) },
      { value: Status.INACTIVE, name: translate(Status.INACTIVE) },
    ],
    []
  );

  const renderUpdateGroupBtn = (groupId: string | number, group: Group) => {
    if (group?.type === GroupType.CUSTOM) {
      return (
        <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
          <div>
            <Icons.edit
              className={classes.button}
              size={18}
              color="#475467"
              onClick={() => {
                groupDrawerRef.current?.open(group);
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const renderViewGroupBtn = (groupId: string | number, group: Group) => {
    if (group?.type === GroupType.SYSTEM) {
      return (
        <Tooltip data-cy="action-edit" title={translate("view")} placement="top">
          <div>
            <Icons.view
              className={classes.button}
              size={18}
              color="#475467"
              onClick={() => {
                groupDrawerRef.current?.open(group);
              }}
            />
          </div>
        </Tooltip>
      );
    }
  };

  const handleFilterBy = (value: string) => {
    let filterValue = [];
    setFilterValue(value);
    switch (value) {
      case showAllStatusCode:
        filterValue = ["0", "1"];
        break;
      case Status.ACTIVE:
        filterValue = ["1"];
        break;
      case Status.INACTIVE:
        filterValue = ["0"];
        break;
      default:
        filterValue = ["0", "1"];
        break;
    }
    handleFilter({ status: filterValue });
  };

  const badgeStatus = (status: number) => (
    <Badge
      className={`
          ${classes.statusBadge}
          ${
            statusConverter(status) === Status.ACTIVE
              ? classes.statusBadgeActive
              : classes.statusBadgeInactive
          }
      `}
    >
      {statusConverter(status)}
    </Badge>
  );

  const columns: Column[] = [
    {
      name: "name",
      title: translate("group"),
      formatComponent: (name) => <span className={classes.rowStyle}>{name}</span>,
    },
    {
      name: "type",
      title: translate("type"),
      formatText: (type) => (
        <span className={classes.rowStyle}>
          {type === GroupType.SYSTEM ? `${GroupType.SYSTEM} default` : type}
        </span>
      ),
    },
    {
      name: "roleName",
      title: translate("accessRule"),
      formatText: (accessRule) => <span className={classes.rowStyle}>{accessRule ?? "_"}</span>,
    },
    {
      name: "countUsers",
      title: translate("members"),
      formatText: (countUsers) => <span className={classes.rowStyle}>{countUsers ?? 0}</span>,
    },
    {
      name: "updatedAt",
      title: translate("lastModified"),
      formatText: (updateAt) => (
        <span className={classes.rowStyle}>
          {updateAt && currentUser?.preferences
            ? formatDateWithUserPref(updateAt, currentUser.preferences)
            : ""}
        </span>
      ),
      hideSearch: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: any) => badgeStatus(status),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "filterBy"],
      right: ["newRecord"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    filterBy: (
      <Select
        className={classes.selectStatus}
        tabIndex={0}
        name="status"
        value={filterValue}
        onChange={(e: SelectChangeEvent<any>) => handleFilterBy(e?.target?.value)}
      >
        {groupStatus?.map((status) => (
          <MenuItem className={classes.selectStatusItem} key={status.value} value={status.value}>
            {status.name}
          </MenuItem>
        ))}
      </Select>
    ),
    newRecord: isShowAction(currentUser, groupPermission.create) && (
      <Button
        className={classes.newRecord}
        customColor={Color.SECONDARY_DARK}
        startIcon={<AddIcon />}
        onClick={() => {
          groupDrawerRef.current?.open({} as Group);
        }}
      >
        <ButtonText>{translate("addNew")}</ButtonText>
      </Button>
    ),
  };

  const handlerowActionsExtraByPermission = () => {
    let rows: RowActionsExtra[] = [
      {
        title: "View",
        key: "viewGroup",
        buttonAndAction: renderViewGroupBtn,
      },
    ];

    if (isShowAction(currentUser, groupPermission.edit)) {
      rows?.push({
        title: "Edit",
        key: "updateGroup",
        buttonAndAction: renderUpdateGroupBtn,
      });
    }

    return rows;
  };

  const rowActionsExtra = handlerowActionsExtraByPermission();

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setGroupPermission({
        edit:
          checkResourceCan(userPermissions, ResourceName.PEOPLE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_GROUPS_EDIT),
        create:
          checkResourceCan(userPermissions, ResourceName.PEOPLE, ResourceCanEnum.EDIT) &&
          hasPermission(userPermissions, FN_NAMES.FUNCTION_SETTINGS_PEOPLE_GROUPS_ADD_NEW),
      });
    }
  }, [userPermissions]);

  useEffect(() => {
    return () => {
      handleFilter({ status: [] });
    };
  }, []);

  useEffect(() => {
    return () => {
      handleFilter({ status: [] });
    };
  }, []);

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request-notification-success-role-changes",
      });
      setHasErrors(undefined);
    }
  }, [hasErrors]);

  return (
    <div className={classes.gridWrapper}>
      <ShipeeziNewGrid
        data={groups}
        columns={columns}
        keyExtractor={(data) => data.id}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        alwaysShowFooter={true}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["updateGroup", "viewGroup"],
        }}
        rowActionsExtra={rowActionsExtra}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />
      <GroupDrawer ref={groupDrawerRef} />
    </div>
  );
};

export default Groups;
